// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { LocationCompanyType } from '@/constants/location-company-type';
import type { Sort } from '@/constants/sort';
import type { PurchaseOrderSortProperty } from '@/purchase-order/hooks/use-purchase-orders';
import { Chip } from '@mui/material';
import { ComponentProps } from 'react';
import { z } from 'zod';

// =============================================================================
// Schemas + types
// =============================================================================

// The line status is a generated column computed from quantities, so it never
// holds the order-level CANCELLED or AWAITING_3PL.
export const poLineItemStatusSchema = z.enum([
  'DRAFT',
  'OPEN_PARTIAL',
  'OPEN_FULL',
  'OPEN_OVERAGE',
  'SHIPPED_PARTIAL',
  'SHIPPED_FULL',
  'SHIPPED_OVERAGE',
  'DELIVERED_PARTIAL',
  'DELIVERED_FULL',
  'DELIVERED_OVERAGE',
  'RECEIVED_PARTIAL',
  'RECEIVED_FULL',
  'RECEIVED_OVERAGE',
]);

export type POLineItemStatus = z.infer<typeof poLineItemStatusSchema>;

export const poPhaseSchema = z.enum([
  'draft',
  'awaiting_3pl',
  'cancelled',
  'open',
  'shipped',
  'delivered',
  'received',
]);
export type POPhase = z.infer<typeof poPhaseSchema>;

export const poSubstatusSchema = z.enum(['partial', 'full', 'overage']);
export type POSubstatus = z.infer<typeof poSubstatusSchema>;

export const poStatusSchema = z.enum([
  'OPEN_PARTIAL',
  'OPEN_FULL',
  'OPEN_OVERAGE',
  'SHIPPED_PARTIAL',
  'SHIPPED_FULL',
  'SHIPPED_OVERAGE',
  'DELIVERED_PARTIAL',
  'DELIVERED_FULL',
  'DELIVERED_OVERAGE',
  'RECEIVED_PARTIAL',
  'RECEIVED_FULL',
  'RECEIVED_OVERAGE',
  'DRAFT',
  'AWAITING_3PL',
  'CANCELLED',
]);
export type POStatus = z.infer<typeof poStatusSchema>;

// =============================================================================
// Chip + phase config
// =============================================================================

type ChipColor = ComponentProps<typeof Chip>['color'];
type ChipVariant = ComponentProps<typeof Chip>['variant'] | 'text';

interface ChipConfig {
  color: ChipColor;
  variant: ChipVariant;
}

interface PhaseConfig {
  label: string;
  backendFilters: POStatus[];
  displayOrder: number;
  chipConfig: ChipConfig;
}

export const PHASE_CONFIG: Record<POPhase, PhaseConfig> = {
  draft: {
    label: 'Draft',
    backendFilters: ['DRAFT'],
    displayOrder: 0,
    chipConfig: { color: 'default', variant: 'text' },
  },
  awaiting_3pl: {
    label: 'Awaiting 3PL',
    backendFilters: ['AWAITING_3PL'],
    displayOrder: 1,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  open: {
    label: 'Open',
    backendFilters: ['OPEN_PARTIAL', 'OPEN_FULL', 'OPEN_OVERAGE'],
    displayOrder: 2,
    chipConfig: { color: 'default', variant: 'filled' },
  },
  shipped: {
    label: 'Shipped',
    backendFilters: ['SHIPPED_PARTIAL', 'SHIPPED_FULL', 'SHIPPED_OVERAGE'],
    displayOrder: 3,
    chipConfig: { color: 'info', variant: 'filled' },
  },
  delivered: {
    label: 'Delivered',
    backendFilters: [
      'DELIVERED_PARTIAL',
      'DELIVERED_FULL',
      'DELIVERED_OVERAGE',
    ],
    displayOrder: 4,
    chipConfig: { color: 'primary', variant: 'filled' },
  },
  received: {
    label: 'Received',
    backendFilters: ['RECEIVED_PARTIAL', 'RECEIVED_FULL', 'RECEIVED_OVERAGE'],
    displayOrder: 5,
    chipConfig: { color: 'success', variant: 'filled' },
  },
  cancelled: {
    label: 'Cancelled',
    backendFilters: ['CANCELLED'],
    displayOrder: 6,
    chipConfig: { color: 'error', variant: 'outlined' },
  },
};

// =============================================================================
// Status predicates
// =============================================================================

export function isFullyReceived(status: POLineItemStatus | POStatus): boolean {
  return status === 'RECEIVED_FULL' || status === 'RECEIVED_OVERAGE';
}

export function isReceived(status: POLineItemStatus | POStatus): boolean {
  return status.startsWith('RECEIVED');
}

export function isCancelled(status: POLineItemStatus | POStatus): boolean {
  return status === 'CANCELLED';
}

export function isProcessed(status: POLineItemStatus | POStatus): boolean {
  return isReceived(status) || isCancelled(status);
}

// =============================================================================
// Status → phase/modifier mapping
// =============================================================================

const STATUS_TO_PHASE: Record<POStatus, POPhase> = Object.fromEntries(
  Object.entries(PHASE_CONFIG).flatMap(([phase, config]) =>
    config.backendFilters.map((status) => [status, phase])
  )
) as Record<POStatus, POPhase>;

export const PHASES_WITH_MODIFIERS: ReadonlySet<POPhase> = new Set<POPhase>([
  'shipped',
  'delivered',
  'received',
]);

export interface ParsedStatus {
  phase: POPhase;
  modifier: POSubstatus | null;
}

export function parseStatus(status: POLineItemStatus | POStatus): ParsedStatus {
  const phase = STATUS_TO_PHASE[status];
  if (!PHASES_WITH_MODIFIERS.has(phase)) return { phase, modifier: null };
  const parts = status.split('_');
  const suffix = parts[parts.length - 1].toLowerCase();
  const parsed = poSubstatusSchema.safeParse(suffix);
  return { phase, modifier: parsed.success ? parsed.data : null };
}

export function getModifierVariant(
  phase: POPhase,
  modifier: POSubstatus | null
): ChipVariant {
  if (modifier === 'partial') return 'outlined';
  return PHASE_CONFIG[phase].chipConfig.variant;
}

// Audit-log labels for a purchase order status. Partial states read as "Partially received"; full and overage
// states collapse to the plain phase ("Open", "Received"), so the audit trail
// never surfaces the overage modifier. Matches FO_STATUS_AUDIT_LABELS, so a
// status every order kind shares reads the same way on each trail.
export const PO_STATUS_AUDIT_LABELS: Record<POStatus, string> =
  Object.fromEntries(
    poStatusSchema.options.map((status) => {
      const parsed = parseStatus(status);
      const phaseLabel = PHASE_CONFIG[parsed.phase].label;
      if (parsed.modifier === 'partial') {
        return [status, `Partially ${phaseLabel.toLowerCase()}`];
      }
      return [status, phaseLabel];
    })
  ) as Record<POStatus, string>;

export const PO_STATUS_NOTIFICATION_LABELS: Record<POStatus, string> =
  Object.fromEntries(
    poStatusSchema.options.map((status) => {
      const parsed = parseStatus(status);
      const phaseLabel = PHASE_CONFIG[parsed.phase].label;
      if (parsed.modifier === 'partial') {
        return [status, `Partially ${phaseLabel.toLowerCase()}`];
      }
      if (parsed.modifier === 'overage') {
        return [status, `${phaseLabel} with overage`];
      }
      return [status, phaseLabel];
    })
  ) as Record<POStatus, string>;

// =============================================================================
// Line item tooltips
// =============================================================================

export const LINE_ITEM_STATUS_TOOLTIPS: Record<POLineItemStatus, string> = {
  DRAFT:
    'This line item is in draft (no shipments have been opened against it)',
  OPEN_PARTIAL:
    'This line item is partially open (shipments have been opened with less than the requested quantity)',
  OPEN_FULL:
    'This line item is fully open (shipments have been opened with exactly the requested quantity)',
  OPEN_OVERAGE:
    'This line item is open with an overage (shipments have been opened with more than the requested quantity)',
  SHIPPED_PARTIAL:
    'This line item is partially shipped (shipments have been shipped with less than the requested quantity)',
  SHIPPED_FULL:
    'This line item is fully shipped (shipments have been shipped with exactly the requested quantity)',
  SHIPPED_OVERAGE:
    'This line item is shipped with an overage (shipments have been shipped with more than the requested quantity)',
  DELIVERED_PARTIAL:
    'This line item is partially delivered (shipments have been delivered with less than the requested quantity)',
  DELIVERED_FULL:
    'This line item is fully delivered (shipments have been delivered with exactly the requested quantity)',
  DELIVERED_OVERAGE:
    'This line item is delivered with an overage (shipments have been delivered with more than the requested quantity)',
  RECEIVED_PARTIAL:
    'This line item is partially received (shipments have been received with less than the requested quantity)',
  RECEIVED_FULL:
    'This line item is fully received (shipments have been received with exactly the requested quantity)',
  RECEIVED_OVERAGE:
    'This line item is received with an overage (shipments have been received with more than the requested quantity)',
};

// =============================================================================
// Phase progression
// =============================================================================

const TERMINAL_PHASES: readonly POPhase[] = ['received', 'cancelled'];

const PHASE_PROGRESSION: readonly POPhase[] = poPhaseSchema.options.filter(
  (p: POPhase) => p !== 'cancelled'
);

export function getPhaseProgression(isThirdPartyEnabled: boolean): POPhase[] {
  return isThirdPartyEnabled
    ? [...PHASE_PROGRESSION]
    : PHASE_PROGRESSION.filter((s) => s !== 'awaiting_3pl');
}

export function getTerminalPhases(): readonly POPhase[] {
  return TERMINAL_PHASES;
}

// =============================================================================
// PO-specific
// =============================================================================

export const PO_STATUS_TOOLTIPS: Record<POStatus, string> = {
  DRAFT:
    'This PO is in draft (no shipments have been opened against any line item)',
  AWAITING_3PL: 'This PO is awaiting an update from your 3PL',
  OPEN_PARTIAL:
    'This PO is partially open (shipments have been opened with less than the requested quantity against one or more line items)',
  OPEN_FULL:
    'This PO is fully open (shipments have been opened with exactly the requested quantity against all line items)',
  OPEN_OVERAGE:
    'This PO is open with an overage (shipments have been opened with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  SHIPPED_PARTIAL:
    'This PO is partially shipped (shipments have been shipped with less than the requested quantity against one or more line items)',
  SHIPPED_FULL:
    'This PO is fully shipped (shipments have been shipped with exactly the requested quantity against all line items)',
  SHIPPED_OVERAGE:
    'This PO is shipped with an overage (shipments have been shipped with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  DELIVERED_PARTIAL:
    'This PO is partially delivered (shipments have been delivered with less than the requested quantity against one or more line items)',
  DELIVERED_FULL:
    'This PO is fully delivered (shipments have been delivered with exactly the requested quantity against all line items)',
  DELIVERED_OVERAGE:
    'This PO is delivered with an overage (shipments have been delivered with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  RECEIVED_PARTIAL:
    'This PO is partially received (shipments have been received with less than the requested quantity against one or more line items)',
  RECEIVED_FULL:
    'This PO is fully received (shipments have been received with exactly the requested quantity against all line items)',
  RECEIVED_OVERAGE:
    'This PO is received with an overage (shipments have been received with the requested quantity on all line items, and more than the requested quantity on at least one line item)',
  CANCELLED: 'This PO is cancelled',
};

export interface PurchaseOrderFilter {
  status?: POPhase;
  subStatuses?: POSubstatus[];
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  sort?: Sort<PurchaseOrderSortProperty>;
  fromLocationIds?: string[];
  fromCustomerIds?: string[];
  fromLocationCompanyTypes?: LocationCompanyType[];
  toLocationIds?: string[];
  toCustomerIds?: string[];
  toLocationCompanyTypes?: LocationCompanyType[];
}
